import { FC } from "react";
import { Divider, Stack } from "@mui/material";
import { useToggle } from "rooks";
import LinkIcon from "@mui/icons-material/Link";
import { ResourceExplorerBillingStatuses } from "./resource-explorer-billing-statuses/ResourceExplorerBillingStatuses";
import { ResourceExplorerResetButton } from "./ResourceExplorerResetButton";
import { ResourceExplorerAppBarSaveButton } from "./ResourceExplorerAppBarSaveButton";
import { ResourceExplorerActionType } from "../../../utils/types/types";
import { SendReportActionItem } from "../../../../common/send-report/SendReportActionItem";
import { ReportsBySourceActionItem } from "../../../../common/reports-by-source/ReportsBySourceActionItem";
import { ReportsBySourceDrawer } from "../../../../common/reports-by-source/ReportsBySourceDrawer";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { ResourceExplorerCsvDownloader } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv/ResourceExplorerCSVDownloader";
import { useAppAbility } from "@/services/permissions";
import { getUserPermissionInfoText } from "@/components/pages/utils/helpers/getUserPermissionInfoText";
import { useAppSelector } from "@/store/hooks";
import { resourceExplorerNameSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/resourceExplorerNameSelector";

interface ResourceExplorerAppBarActionsProps {
  viewId: string;
  loading?: boolean;
  onSaveClick(type: ResourceExplorerActionType): void;
}

export const ResourceExplorerAppBarActions: FC<
  ResourceExplorerAppBarActionsProps
> = ({ onSaveClick, viewId, loading }) => {
  const [reportsDrawerOpen, toggleReportsDrawerOpen] = useToggle(false);
  const name = useAppSelector(resourceExplorerNameSelector);

  const { cannot } = useAppAbility();
  const cannotEditResourceExplorerView = cannot("edit", "resource-explorer");
  const userPermissionTooltipText = cannotEditResourceExplorerView
    ? getUserPermissionInfoText("Send Report")
    : undefined;

  const resourceExplorerUrl = `${window.location.origin}${window.location.pathname}`;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <ResourceExplorerBillingStatuses />

      <Divider orientation="vertical" flexItem />

      <ResourceExplorerCsvDownloader trigger="button" color="primary" />
      <CopyIconButton
        data={resourceExplorerUrl}
        TooltipProps={{ title: "Copy URL", placement: "top" }}
      >
        <LinkIcon fontSize="small" color="primary" />
      </CopyIconButton>

      <SendReportActionItem
        iconButton
        sourceType="view"
        sourceId={viewId}
        sourceName={name ?? ""}
        disabledTooltipText={userPermissionTooltipText}
        disabled={loading || cannotEditResourceExplorerView}
      />

      <ReportsBySourceActionItem
        sourceId={viewId}
        sourceType="view"
        onClick={toggleReportsDrawerOpen}
      />

      <ResourceExplorerResetButton />

      <ResourceExplorerAppBarSaveButton
        openDialog={onSaveClick}
        viewId={viewId}
      />

      <ReportsBySourceDrawer
        sourceId={viewId}
        open={reportsDrawerOpen}
        sourceType="view"
        toggleOpen={toggleReportsDrawerOpen}
      />
    </Stack>
  );
};
